import { useState } from "react";
import { motion } from "framer-motion";
import { Headphones, Users } from "lucide-react";
import { M, EASE } from "../lib/site";
import { MaskLines, FadeUp } from "./Reveal";
import { Field, useSubmit, SubmitButton } from "./FormBits";

const DJForm = () => {
  const { state, onSubmit } = useSubmit("/apply/dj", "Application sent — we'll be in touch.");
  return (
    <form onSubmit={onSubmit} data-testid="dj-form" className="grid gap-8 sm:grid-cols-2">
      <Field label="Name" name="name" required testid="dj-name-input" placeholder="Your full name" />
      <Field label="DJ name" name="dj_name" required testid="dj-djname-input" placeholder="Your artist name" />
      <Field label="Mobile" name="mobile" type="tel" required testid="dj-mobile-input" placeholder="04xx xxx xxx" />
      <Field label="Email" name="email" type="email" required testid="dj-email-input" placeholder="you@email.com" />
      <Field label="Where do you currently play?" name="current_venues" required testid="dj-venues-input" placeholder="Venues, residencies, events" className="sm:col-span-2" />
      <Field label="Genre(s)" name="genres" required testid="dj-genres-input" placeholder="R&B, Afro House, Amapiano…" className="sm:col-span-2" />
      <Field label="SoundCloud" name="soundcloud" testid="dj-soundcloud-input" placeholder="soundcloud.com/…" />
      <Field label="Instagram" name="instagram" testid="dj-instagram-input" placeholder="@yourhandle" />
      <Field label="Other socials / mixes" name="other_socials" testid="dj-socials-input" placeholder="TikTok, Mixcloud, YouTube…" className="sm:col-span-2" />
      <Field label="Anything else?" name="notes" as="textarea" rows={3} testid="dj-notes-input" placeholder="Tell us about your sound" className="sm:col-span-2" />
      <div className="sm:col-span-2"><SubmitButton state={state} label="Apply to the DJ team" testid="dj-submit-btn" /></div>
    </form>
  );
};

const PromoterForm = () => {
  const { state, onSubmit } = useSubmit("/apply/promoter", "Application sent — we'll be in touch.");
  return (
    <form onSubmit={onSubmit} data-testid="promoter-form" className="grid gap-8 sm:grid-cols-2">
      <Field label="Name" name="name" required testid="promoter-name-input" placeholder="Your full name" />
      <Field label="Instagram" name="instagram" testid="promoter-instagram-input" placeholder="@yourhandle" />
      <Field label="Mobile" name="mobile" type="tel" required testid="promoter-mobile-input" placeholder="04xx xxx xxx" />
      <Field label="Email" name="email" type="email" required testid="promoter-email-input" placeholder="you@email.com" />
      <Field label="Where are you currently promoting / hosting?" name="current_venues" required testid="promoter-venues-input" placeholder="Venues, nights, events" className="sm:col-span-2" />
      <Field label="Usual guest list numbers" name="guest_numbers" required testid="promoter-numbers-input" placeholder="e.g. 20–40 per night" />
      <Field label="Other socials" name="other_socials" testid="promoter-socials-input" placeholder="TikTok, etc." />
      <Field label="Experience & notes" name="notes" as="textarea" rows={3} testid="promoter-notes-input" placeholder="Tell us about your crowd" className="sm:col-span-2" />
      <div className="sm:col-span-2"><SubmitButton state={state} label="Apply to the promo team" testid="promoter-submit-btn" /></div>
    </form>
  );
};

const TABS = [
  { key: "dj", label: "DJ Team", icon: Headphones },
  { key: "promoter", label: "Promo / Host", icon: Users },
];

export const JoinTeam = () => {
  const [tab, setTab] = useState("dj");
  return (
    <section id="team" className="relative overflow-hidden border-t border-[var(--line)] py-24 sm:py-36" data-testid="team-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-10">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-6">04 — Join The Team</p>
            <MaskLines lines={["Join the", "Team."]}
              className="font-display text-6xl leading-[0.9] text-[#F8F6F0] sm:text-6xl lg:text-7xl" />
            <FadeUp delay={0.15}>
              <p className="mt-8 max-w-md text-base leading-relaxed text-[var(--stone)] sm:text-lg">
                DJs, promoters and hosts — if you move a room, we want to hear from you.
              </p>
              <div className="relative mt-10 aspect-[4/5] overflow-hidden rounded-[28px] lg:max-h-[620px]">
                <img src={M.team} alt="Two guests sharing a toast under the mirror-ball ceiling" loading="lazy" className="h-full w-full object-cover object-[50%_35%]" data-testid="team-photo" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
            </FadeUp>
          </div>

          <FadeUp className="lg:col-span-7" delay={0.1}>
            <div className="rounded-[28px] border border-[var(--line)] bg-[var(--card)]/70 p-7 backdrop-blur-xl sm:p-12">
              <div className="relative mb-10 grid grid-cols-2 rounded-full border border-[var(--line)] p-1.5" role="tablist">
                {TABS.map(({ key, label, icon: Icon }) => (
                  <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)} data-testid={`${key}-apply-tab`}
                    className={`relative z-10 flex items-center justify-center gap-2 rounded-full py-3.5 text-[12px] font-semibold uppercase tracking-[0.16em] transition-colors duration-500 sm:text-[13px] ${tab === key ? "text-[#09080B]" : "text-[#F8F6F0]/75 hover:text-[#F8F6F0]"}`}>
                    {tab === key && <motion.span layoutId="tab-pill" transition={{ duration: 0.6, ease: EASE }} className="absolute inset-0 -z-10 rounded-full bg-[var(--glow)]" />}
                    <Icon size={15} /> {label}
                  </button>
                ))}
              </div>
              <motion.div key={tab} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
                {tab === "dj" ? <DJForm /> : <PromoterForm />}
              </motion.div>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};
